import React, { useState, ChangeEvent } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

interface RadioProps {
  name: string;
  children?: React.ReactNode;
  options?: {
    label: string;
    value: number | string;
  }[];
  required?: boolean;
  labelWithinRow?: string;
  customOption?: boolean;
  noBorder?: boolean;
  className?: string;
  labelClassName?: string;
  checkmarkClassName?: string;
  resetValue?: () => void;
  customColClass?: string;
  defaultChecked?: any;
  setSelectedValue?: (value: any) => void;
  customOnChange?: (value: any) => void;
  eventName?: string;
  eventContext?: string;
  disabled?: boolean;
  testIdPrefix?: string;
}

const Radio = function ({
  name,
  children,
  required,
  options,
  className,
  labelClassName,
  checkmarkClassName,
  resetValue,
  customColClass,
  customOnChange,
  defaultChecked,
  setSelectedValue,
  noBorder,
  eventName: _eventName,
  eventContext: _eventContext,
  disabled,
  testIdPrefix,
  ...props
}: RadioProps) {
  const {
    register,
    setValue,
    getValues,
    // unregister,
    formState: { errors },
  } = useFormContext();

  const [val, setVal] = useState(getValues(name) ? getValues(name) : "");

  function onValueChange(name: string, e: ChangeEvent<HTMLInputElement>) {
    if (setSelectedValue) setSelectedValue(e.target.value);
    setVal(e.target.value);
    setValue(name, e.target.value);
  }

  function constructErrorName() {
    if (!name) return;
    let n = name.split(/[[.]+/).join(",").split(/]|,/);
    n = n.filter((each) => each.length > 0);
    return errors?.[n[0]]?.[n[1]]?.[n[2]];
  }

  const GetLabel = function () {
    return (
      <Col lg={4} className="position-relative align-items-center d-flex flex-row">
        <label className="mb-0 text-primary">{props.labelWithinRow}</label>
      </Col>
    );
  };

  return (
    <Row
      className={`bg-transparent br-5 align-items-center border-${required && constructErrorName() ? "red" : "blue"} ${
        noBorder ? "" : "form-control"
      } d-flex ${props.customOption ? "" : "flex-column"} pt-10 pb-10 pl-0 pr-0 bg-transparent height-auto m-0 ${className}`}
    >
      {props.labelWithinRow && <GetLabel />}

      {options &&
        options.map(({ value, label }, i) => (
          <Col key={label} className={customColClass || ""}>
            <label
              className={`font-weight-normal position-relative d-flex flex-row align-items-center cursor-pointer ${
                props.customOption ? "mb-0" : "mb-10"
              } ${labelClassName} ${i === options.length - 1 ? null : "mb-0"}`}
            >
              <input
                type="radio"
                className="position-absolute cursor-pointer sr-only"
                value={value}
                data-testid={testIdPrefix ? `${testIdPrefix}-${value}` : undefined}
                {...register(name, { required })}
                onChange={(e) => {
                  if (customOnChange) customOnChange(e);
                  onValueChange(name, e);
                  if (resetValue) resetValue();
                }}
                name={name}
                defaultChecked={!defaultChecked ? i === 0 : val === value}
                disabled={disabled && !(!defaultChecked ? i === 0 : val === value)}
              />
              <span className={`checkmark ${checkmarkClassName} ${disabled ? "disabled" : ""}`} />
              <span className="mb-0">{label}</span>
            </label>
          </Col>
        ))}
      {(children && val === "yes") || val === "True" ? children : null}
    </Row>
  );
};

Radio.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  required: PropTypes.bool,
  labelWithinRow: PropTypes.string,
  customOption: PropTypes.bool,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  checkmarkClassName: PropTypes.string,
  resetValue: PropTypes.func,
  customColClass: PropTypes.string,
  setSelectedValue: PropTypes.func,
  eventContext: PropTypes.string,
  eventName: PropTypes.string,
};

Radio.defaultProps = {
  children: null,
  options: [],
  required: false,
  labelWithinRow: "",
  customOption: false,
  className: "",
  labelClassName: "",
  checkmarkClassName: "",
  resetValue: null,
  customColClass: "",
  defaultChecked: "",
  noBorder: false,
  setSelectedValue: null,
  eventContext: "Quote Request Form",
  eventName: "",
  customOnChange: null,
  disabled: false,
};

export default Radio;
